import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Organizer } from 'modules/organizer/domain/interfaces/organizer';
import { toApiError } from 'shared/errors';
import { fetchOrganizer, resetOrganizer, upsertOrganizer } from './actions';

type OrganizerSliceState = {
  isLoading: boolean;
  isTeamCreating: boolean;
  isTeamEditing: boolean;
  isSaving: boolean;
  info: Organizer | null;
  /**
   * Организация, по которой отработал последний запрос — независимо от того, удался он или нет.
   * Булева `isLoaded` не отвечала на вопрос «чья это загрузка»: после неудачи по организации A
   * переключение на B ничего не перезапрашивало. `isLoaded` осталась производным селектором.
   */
  loadedTournamentId: number | null;
  error: string | null;
};

type OrganizerReducerBuilder = ActionReducerMapBuilder<OrganizerSliceState>;

const initialState: OrganizerSliceState = {
  isLoading: false,
  isTeamCreating: false,
  isTeamEditing: false,
  isSaving: false,
  info: null,
  loadedTournamentId: null,
  error: null,
};

/**
 * Данные прежней организации к запрашиваемой не относятся: пока едет новая — и тем более
 * если запрос упал — показывать их нельзя. Иначе вкладка «Профиль» рисует форму правки
 * чужой организации, и «Сохранить» уходит с её `id`, перезаписывая её данные формой.
 */
function dropStaleInfo(state: OrganizerSliceState, tournamentId: number) {
  if (state.info && state.info.id !== tournamentId) {
    state.info = null;
  }
}

function createFetchOrganizerReducer(builder: OrganizerReducerBuilder) {
  builder.addCase(fetchOrganizer.pending, (state, action) => {
    state.isLoading = true;
    state.error = null;
    dropStaleInfo(state, action.meta.arg.tournamentId);
  });

  builder.addCase(fetchOrganizer.fulfilled, (state, action) => {
    state.isLoading = false;
    state.loadedTournamentId = action.meta.arg.tournamentId;
    state.error = null;
    state.info = action.payload;
  });

  // `loadedTournamentId` пишется и при неудаче: иначе эффект в `use-fetch-organizer`
  // немедленно повторил бы упавший запрос и зациклился на лежащем бэкенде.
  // Повтор — только руками, через `refetch()`.
  builder.addCase(fetchOrganizer.rejected, (state, action) => {
    state.isLoading = false;
    state.loadedTournamentId = action.meta.arg.tournamentId;
    state.error = toApiError(action.payload ?? action.error).message || 'Unknown error';
    dropStaleInfo(state, action.meta.arg.tournamentId);
  });
}

function createUpsertOrganizerReducer(builder: OrganizerReducerBuilder) {
  builder.addCase(upsertOrganizer.pending, (state) => {
    state.isSaving = true;
  });

  builder.addCase(upsertOrganizer.fulfilled, (state, action) => {
    state.isSaving = false;
    state.info = action.payload;
    state.loadedTournamentId = action.payload.id;
    state.error = null;
  });

  builder.addCase(upsertOrganizer.rejected, (state) => {
    state.isSaving = false;
  });
}

function createResetOrganizerReducer(builder: OrganizerReducerBuilder) {
  builder.addCase(resetOrganizer, (state) => {
    state.isLoading = false;
    state.info = null;
    state.loadedTournamentId = null;
    state.error = null;
  });
}

const slice = createSlice({
  name: 'organizer',
  initialState,
  reducers: {
    removeChampionshipFromList: (state, action: PayloadAction<{ id: number }>) => {
      if (!state.info) return;
      state.info.latestChampionships = state.info.latestChampionships.filter((t) => t.id !== action.payload.id);
    },
  },
  extraReducers: (builder) => {
    createFetchOrganizerReducer(builder);
    createUpsertOrganizerReducer(builder);
    createResetOrganizerReducer(builder);
  },
});

export const { removeChampionshipFromList } = slice.actions;

const organizerReducer = slice.reducer;
export default organizerReducer;
